import { gql } from '@apollo/client';
import {
  ARCHIVED_TOPICS,
  SPACE,
  SPACE_TAGS,
  SPACE_MEMBERS,
  TOPIC,
  VIEWER,
  VIEWER_SESSIONS,
} from './queries';

export const archiveTopicCache = (spaceVars, spaceSlug, cache, args) => {
  const data = cache.readQuery({
    query: SPACE,
    variables: {
      slug: spaceSlug,
      ...spaceVars,
    },
  });

  if (data) {
    const space = {
      ...data.space,
      topics: data.space.topics.filter(({ id }) => args.id !== id),
    };

    cache.writeQuery({
      query: SPACE,
      data: { space },
      variables: {
        slug: spaceSlug,
        ...spaceVars,
      },
    });
  }

  const archiveData = cache.readQuery({
    query: ARCHIVED_TOPICS,
    variables: { slug: spaceSlug },
  });

  if (archiveData) {
    const space = {
      ...archiveData.space,
      topics: [args, ...archiveData.space.topics].sort(
        (a, b) => b.archivedAt - a.archivedAt
      ),
    };

    cache.writeQuery({
      query: ARCHIVED_TOPICS,
      data: { space },
      variables: { slug: spaceSlug },
    });
  }
};

export const createCommentCache = (topicId, cache, args) => {
  const topic = cache.identify({
    __typename: 'Topic',
    id: topicId,
  });

  if (topic) {
    cache.modify({
      id: topic,
      fields: {
        comments: (commentRefs = [], { readField }) => {
          const newCommentRef = cache.writeFragment({
            data: { __typename: 'Comment', ...args },
            fragment: gql`
              fragment CreateComment on Comment {
                id
                body
                createdAt
                author {
                  id
                  name
                  avatarUrl
                }
              }
            `,
          });

          if (commentRefs.some((ref) => readField('id', ref) === args.id)) {
            return commentRefs;
          }

          return [...commentRefs, newCommentRef];
        },
      },
    });
  }
};

export const createSessionCache = (cache, args) => {
  const data = cache.readQuery({
    query: VIEWER_SESSIONS,
  });

  if (data) {
    const user = cache.identify({ __typename: 'User', id: data.viewer.id });

    cache.modify({
      id: user,
      fields: {
        sessions: (sessionRefs = [], { readField }) => {
          const newSessionRef = cache.writeFragment({
            data: { __typename: 'Session', ...args },
            fragment: gql`
              fragment CreateSession on Session {
                id
                createdAt
                userAgent
              }
            `,
          });

          if (sessionRefs.some((ref) => readField('id', ref) === args.id)) {
            return sessionRefs;
          }

          return [newSessionRef, ...sessionRefs].sort((a, b) => {
            return readField('createdAt', b) - readField('createdAt', a);
          });
        },
      },
    });
  }
};

export const createSpaceCache = (cache, args) => {
  const data = cache.readQuery({
    query: VIEWER,
  });

  if (data) {
    cache.modify({
      id: cache.identify({ __typename: 'User', id: data.viewer.id }),
      fields: {
        spaces: (spaceRefs = [], { readField }) => {
          const newSpaceRef = cache.writeFragment({
            data: { __typename: 'Space', ...args },
            fragment: gql`
              fragment CreateSpace on Space {
                id
                name
                slug
              }
            `,
          });

          if (spaceRefs.some((ref) => readField('id', ref) === args.id)) {
            return spaceRefs;
          }

          return [...spaceRefs, newSpaceRef].sort((aRef, bRef) => {
            return readField('name', aRef).localeCompare(
              readField('name', bRef)
            );
          });
        },
      },
    });
  }
};

export const createSpaceMemberCache = (spaceSlug, cache, args) => {
  const data = cache.readQuery({
    query: SPACE_MEMBERS,
    variables: { slug: spaceSlug },
  });

  if (data) {
    const space = cache.identify({ __typename: 'Space', id: data.space.id });

    cache.modify({
      id: space,
      fields: {
        members: (memberRefs = [], { readField }) => {
          const newMemberRef = cache.writeFragment({
            data: { __typename: 'SpaceMember', ...args },
            fragment: gql`
              fragment CreateSpaceMember on SpaceMember {
                spaceId
                joinedAt
                role
                user {
                  id
                  name
                  avatarUrl
                }
              }
            `,
          });

          if (
            memberRefs.some((ref) => readField('user', ref).id === args.user.id)
          ) {
            return memberRefs;
          }

          return [...memberRefs, newMemberRef].sort((aRef, bRef) => {
            const aRefName = readField('user', aRef).name;
            const bRefName = readField('user', bRef).name;

            return aRefName.localeCompare(bRefName);
          });
        },
      },
    });
  }
};

export const createTagCache = (spaceSlug, cache, args) => {
  const data = cache.readQuery({
    query: SPACE_TAGS,
    variables: { slug: spaceSlug },
  });

  if (data) {
    const space = cache.identify({ __typename: 'Space', id: data.space.id });

    cache.modify({
      id: space,
      fields: {
        tags: (tagRefs = [], { readField }) => {
          const newTagRef = cache.writeFragment({
            data: { __typename: 'Tag', ...args },
            fragment: gql`
              fragment CreateTag on Tag {
                id
                name
              }
            `,
          });

          if (tagRefs.some((ref) => readField('id', ref) === args.id)) {
            return tagRefs;
          }

          return [...tagRefs, newTagRef].sort((aRef, bRef) => {
            return readField('name', aRef).localeCompare(
              readField('name', bRef)
            );
          });
        },
      },
    });
  }
};

export const createTopicCache = (spaceId, cache, args) => {
  const space = cache.identify({
    __typename: 'Space',
    id: spaceId,
  });

  if (space) {
    cache.modify({
      id: space,
      fields: {
        topics: (topicRefs = [], { readField }) => {
          const newTopicRef = cache.writeFragment({
            data: { __typename: 'Topic', ...args },
            fragment: gql`
              fragment CreateTopic on Topic {
                id
                name
                state
                assignee {
                  id
                  name
                  avatarUrl
                }
                author {
                  id
                  name
                  avatarUrl
                }
                tags {
                  id
                  name
                }
                createdAt
                publicId
                isPinned
              }
            `,
          });

          if (topicRefs.some((ref) => readField('id', ref) === args.id)) {
            return topicRefs;
          }

          return [...topicRefs, newTopicRef].sort((a, b) => {
            const aIsPinned = readField('isPinned', a);
            const bIsPinned = readField('isPinned', b);
            const aCreatedAt = readField('createdAt', a);
            const bCreatedAt = readField('createdAt', b);

            if (aIsPinned) {
              return -1;
            } else if (bIsPinned) {
              return 1;
            }

            return bCreatedAt - aCreatedAt;
          });
        },
      },
    });
  }
};

export const deleteCommentCache = (spaceSlug, topicPublicId, cache, args) => {
  const data = cache.readQuery({
    query: TOPIC,
    variables: {
      spaceSlug: spaceSlug,
      publicId: parseInt(topicPublicId),
    },
  });

  if (data) {
    const topic = {
      ...data.topic,
      comments: data.topic.comments.filter(({ id }) => args.id !== id),
    };

    cache.writeQuery({
      query: TOPIC,
      data: { topic },
      variables: {
        spaceSlug: spaceSlug,
        publicId: parseInt(topicPublicId),
      },
    });
  }
};

export const deleteSpaceCache = (spaceSlug, cache, args) => {
  const data = cache.readQuery({
    query: VIEWER,
  });

  if (data) {
    const viewer = {
      ...data.viewer,
      spaces: data.viewer.spaces.filter(({ id }) => args.id !== id),
    };

    cache.writeQuery({
      query: VIEWER,
      data: { viewer },
    });
  }

  if (spaceSlug === args.slug) {
    window.location.replace('/login');
  }
};

export const deleteSpaceMemberCache = (spaceSlug, cache, args, viewerId) => {
  const data = cache.readQuery({
    query: SPACE_MEMBERS,
    variables: { slug: spaceSlug },
  });

  if (data) {
    const space = {
      ...data.space,
      members: data.space.members.filter(
        (spaceMember) =>
          `${spaceMember.user.id}:${spaceMember.spaceId}` !==
          `${args.user.id}:${args.spaceId}`
      ),
    };

    cache.writeQuery({
      query: SPACE_MEMBERS,
      data: { space },
      variables: { slug: spaceSlug },
    });
  }

  if (viewerId === args.user.id) {
    deleteSpaceCache(spaceSlug, cache, { id: args.spaceId, slug: spaceSlug });
  }
};

export const deleteTagCache = (spaceSlug, cache, args) => {
  const data = cache.readQuery({
    query: SPACE_TAGS,
    variables: { slug: spaceSlug },
  });

  const space = {
    ...data.space,
    tags: data.space.tags.filter((tag) => tag.id !== args.id),
  };

  cache.writeQuery({
    query: SPACE_TAGS,
    data: { space },
    variables: { slug: spaceSlug },
  });
};

export const deleteSessionCache = (cache, args) => {
  const data = cache.readQuery({
    query: VIEWER_SESSIONS,
  });

  if (data) {
    const viewer = {
      ...data.viewer,
      sessions: data.viewer.sessions.filter(
        (session) => session.id !== args.id
      ),
    };

    cache.writeQuery({
      query: VIEWER_SESSIONS,
      data: { viewer },
    });
  }

  const viewerData = cache.readQuery({
    query: VIEWER,
  });

  if (viewerData && viewerData.viewer.currentSession === args.id) {
    localStorage.removeItem('token');
    window.location.replace('/');
  }
};

export const deleteTopicCache = (spaceVars, spaceSlug, cache, args) => {
  const data = cache.readQuery({
    query: SPACE,
    variables: {
      slug: spaceSlug,
      ...spaceVars,
    },
  });

  if (data) {
    const space = {
      ...data.space,
      topics: data.space.topics.filter(({ id }) => args.id !== id),
    };

    cache.writeQuery({
      query: SPACE,
      data: { space },
      variables: {
        slug: spaceSlug,
        ...spaceVars,
      },
    });
  }

  const archiveData = cache.readQuery({
    query: ARCHIVED_TOPICS,
    variables: { slug: spaceSlug },
  });

  if (archiveData) {
    const space = {
      ...archiveData.space,
      topics: archiveData.space.topics.filter(({ id }) => args.id !== id),
    };

    cache.writeQuery({
      query: ARCHIVED_TOPICS,
      data: { space },
      variables: { slug: spaceSlug },
    });
  }
};

export const deleteViewerCache = () => {
  localStorage.removeItem('token');
  window.location.replace('/');
};

export const updateCommentBodyCache = (cache, args) => {
  cache.modify({
    id: cache.identify({ __typename: 'Comment', id: args.id }),
    fields: {
      body: (body) => {
        return args.body !== undefined ? args.body : body;
      },
      lastEdited: (lastEdited) => {
        return args.lastEdited !== undefined ? args.lastEdited : lastEdited;
      },
      versions: (versionRefs = [], { readField }) => {
        const newVersionRef = cache.writeFragment({
          data: { __typename: 'Version', ...args.newVersion },
          fragment: gql`
            fragment CreateVersion on Version {
              id
              body
              publicId
              createdAt
              author {
                id
                name
                avatarUrl
              }
            }
          `,
        });

        if (
          versionRefs.some((ref) => readField('id', ref) === args.newVersion.id)
        ) {
          return versionRefs;
        }

        return [newVersionRef, ...versionRefs];
      },
    },
  });
};

export const updateSpaceCache = (
  cache,
  args,
  spaceId,
  spaceRole,
  spaceSlug
) => {
  cache.modify({
    id: cache.identify({ __typename: 'Space', id: args.id }),
    fields: {
      customer: (customer) => {
        return args.customer !== undefined ? args.customer : customer;
      },
      bannerUrl: (bannerUrl) => {
        return args.bannerUrl !== undefined ? args.bannerUrl : bannerUrl;
      },
      earlyAccess: (earlyAccess) => {
        return args.earlyAccess !== undefined ? args.earlyAccess : earlyAccess;
      },
      icon: (icon) => {
        return args.icon !== undefined ? args.icon : icon;
      },
      name: (name) => {
        return args.name !== undefined ? args.name : name;
      },
      planLevel: (planLevel) => {
        return args.planLevel !== undefined ? args.planLevel : planLevel;
      },
      public: (isPublic) => {
        // prettier-ignore
        return args.public !== undefined
        ? (args.public === false && !spaceRole
            ? deleteSpaceCache(spaceSlug, cache, { id: spaceId, slug: spaceSlug })
            : args.public)
        : isPublic;
      },
      slug: (slug) => {
        return args.slug !== undefined ? args.slug : slug;
      },
      ownerId: (ownerId) => {
        return args.ownerId !== undefined ? args.ownerId : ownerId;
      },
      joinCode: (joinCode) => {
        return args.joinCode !== undefined ? args.joinCode : joinCode;
      },
    },
  });
};

export const updateSpaceMemberCache = (cache, args, viewerId) => {
  const spaceMember = cache.identify({
    __typename: 'SpaceMember',
    user: { id: args.user.id },
    spaceId: args.spaceId,
  });

  if (spaceMember && args.role) {
    cache.modify({
      id: spaceMember,
      fields: {
        role: (role) => {
          return args.role !== undefined ? args.role : role;
        },
      },
    });
  }

  const user = cache.identify({
    __typename: 'User',
    id: args.user.id,
  });

  if (user) {
    cache.modify({
      id: user,
      fields: {
        avatarUrl: (avatarUrl) => {
          return args.user.avatarUrl !== undefined
            ? args.user.avatarUrl
            : avatarUrl;
        },
        name: (name) => {
          return args.user.name !== undefined ? args.user.name : name;
        },
      },
    });
  }

  if (viewerId === args.user.id) {
    cache.modify({
      id: cache.identify({ __typename: 'Space', id: args.spaceId }),
      fields: {
        role: (role) => {
          return args.role !== undefined ? args.role : role;
        },
      },
    });
  }
};

export const updateTagCache = (cache, args) => {
  cache.modify({
    id: cache.identify({ __typename: 'Tag', id: args.id }),
    fields: {
      name: (name) => {
        return args.name !== undefined ? args.name : name;
      },
    },
  });
};

export const updateTopicCache = (spaceVars, spaceSlug, cache, args) => {
  cache.modify({
    id: cache.identify({ __typename: 'Topic', id: args.id }),
    fields: {
      archivedAt: (archivedAt) => {
        return args.archivedAt !== undefined ? args.archivedAt : archivedAt;
      },
      assignee: (assignee) => {
        return args.assignee !== undefined ? args.assignee : assignee;
      },
      isPinned: (isPinned) => {
        return args.isPinned !== undefined ? args.isPinned : isPinned;
      },
      name: (name) => {
        return args.name !== undefined ? args.name : name;
      },
      state: (state) => {
        return args.state !== undefined ? args.state : state;
      },
      tags: (tags) => {
        return args.tags !== undefined ? args.tags : tags;
      },
    },
  });

  if (args.archivedAt !== undefined && args.archivedAt !== null) {
    archiveTopicCache(spaceVars, spaceSlug, cache, args);
  }

  if (args.archivedAt === null) {
    unarchiveTopicCache(spaceVars, spaceSlug, cache, args);
  }

  if (args.isPinned !== undefined) {
    const data = cache.readQuery({
      query: SPACE,
      variables: {
        slug: spaceSlug,
        ...spaceVars,
      },
    });

    if (data) {
      const space = {
        ...data.space,
        topics: [...data.space.topics].sort((a, b) => {
          if (a.isPinned) {
            return -1;
          } else if (b.isPinned) {
            return 1;
          }

          return b.createdAt - a.createdAt;
        }),
      };

      cache.writeQuery({
        query: SPACE,
        data: { space },
        variables: {
          slug: spaceSlug,
          ...spaceVars,
        },
      });
    }
  }
};

export const updateViewerCache = (cache, args) => {
  cache.modify({
    id: cache.identify({ __typename: 'User', id: args.id }),
    fields: {
      avatarUrl: (avatarUrl) => {
        return args.avatarUrl !== undefined ? args.avatarUrl : avatarUrl;
      },
      email: (email) => {
        return args.email !== undefined ? args.email : email;
      },
      name: (name) => {
        return args.name !== undefined ? args.name : name;
      },
      nightMode: (nightMode) => {
        return args.nightMode !== undefined ? args.nightMode : nightMode;
      },
    },
  });
};

export const unarchiveTopicCache = (
  spaceVars,
  spaceSlug,
  cache,
  unarchiveTopic
) => {
  const data = cache.readQuery({
    query: SPACE,
    variables: {
      slug: spaceSlug,
      ...spaceVars,
    },
  });

  if (data) {
    const space = {
      ...data.space,
      topics: [unarchiveTopic, ...data.space.topics].sort((a, b) => {
        if (a.isPinned) {
          return -1;
        } else if (b.isPinned) {
          return 1;
        }

        return b.createdAt - a.createdAt;
      }),
    };

    cache.writeQuery({
      query: SPACE,
      data: { space },
      variables: {
        slug: spaceSlug,
        ...spaceVars,
      },
    });
  }

  const archiveData = cache.readQuery({
    query: ARCHIVED_TOPICS,
    variables: { slug: spaceSlug },
  });

  if (archiveData) {
    const space = {
      ...archiveData.space,
      topics: archiveData.space.topics.filter(
        ({ id }) => unarchiveTopic.id !== id
      ),
    };

    cache.writeQuery({
      query: ARCHIVED_TOPICS,
      data: { space },
      variables: { slug: spaceSlug },
    });
  }
};
